/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { QPATResponse } from 'qp-ui-interface';
import { BehaviorSubject, forkJoin, from, Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { NewrelicService } from 'src/app/analytics/newrelic/service/newrelic.service';
import { LocalStorageKeys } from '../../LocalStorageKeys';
import { getXClientId } from '../../utils/deviceUtils';
import { saveXAuthToken } from '../../utils/tokenUtils';
import { ConfigService } from '../config/config.service';
import { DeviceHandlerService } from '../device-handler/device-handler.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { QPATService } from '../qpat/qpat.service';
import { SubscriptionHandlerService } from '../subscription-handler.service';
import {
  ClientRegistration,
  ClientToken,
  MVPDData,
  PreauthorizedResourcesResponse,
  RegCodeResponse,
  ShortMediaTokenResponse,
  UserMetadata,
  UserResponse,
} from './adobe.interface';

declare global {
  interface Window {
    env: string;
  }
}

@Injectable()
export class AdobeService {
  // TVE SESSION INFO
  private clientToken: ClientToken;
  private clientRegistration: ClientRegistration;
  private authorizedEntitlements: string[];
  private userMetadata: UserMetadata;
  private mvpdHash: string;
  private userResponseDefault = {
    userId: '',
    expires: 0,
  };
  private userResponse: BehaviorSubject<UserResponse> = new BehaviorSubject<UserResponse>(this.userResponseDefault);
  private shortMediaToken: string;
  private refreshTokenTimeout: ReturnType<typeof setTimeout>;

  // ADOBE SERVICE
  private apiUrl: string;
  private requestorId: string;
  private softwareStatement =
    'eyJhbGciOiJSUzI1NiJ9.eyJzdWIiOiIzYTM0YWRlNi00ZjhhLTRmMGItYjc5Ni1jNDY4N2VmMTQ3ZWYiLCJuYmYiOjE3MjA3MDkzMDUsImlzcyI6ImF1dGguYWRvYmUuY29tIiwiaWF0IjoxNzIwNzA5MzA1fQ.XaKHkScU0wVAO0fwgyhCRcMIWftWopLiD5xipbmMXHjXKJzRCykSy1kvv280huhEQgog_wmbSxhX4npAIS3HgyP8TMAdBBQlkc_kqnuVdTD4zYlKHiPCDigWPl03MP4seb4iKHlrSpoNJeYjDIDJqmFWSbPYWPLBkwnR6-lr0s3O7EXb-jLAoIOdOkN5d0dweOEL8TzqQmFR3DqHjITI8g7MTPZJ9nVpaaeaBkEiHvkjhCRINjc2YHRWeculcCU0sAETSF09ORlzeQWsHSAoA8pRLvuIKNjPbzn10wnWhYR_QWYFsS04jiDEGkFI0siA2TFCZh8rUaVFDW8jlwFIvg';
  private preAuthorizedResources: string[] = ['univision', 'galavision', 'deportes'];

  // MVPD selected image
  private mvpdConfigListUrl: string;
  private mvpdImageSrc: string;

  private serviceName = 'AdobeService';

  constructor(
    private http: HttpClient,
    private deviceHandler: DeviceHandlerService,
    private readonly configService: ConfigService,
    private localStorage: LocalStorageService,
    private qpatService: QPATService,
    private subscriptionHandlerService: SubscriptionHandlerService,
    private newrelic: NewrelicService
  ) {
    // this.apiUrl = environment.production ? 'http://api.auth.adobe.com' : 'http://api.auth-staging.adobe.com';
    this.apiUrl = 'https://api.auth.adobe.com';
    this.requestorId = this.configService.applicationConfig?.requestorId ?? 'univision';
    this.mvpdConfigListUrl =
      this.configService.applicationConfig?.adobeConfigUrl ?? window.env === 'prod'
        ? 'https://data-store.api-cms.prd.univisionnow.com/content/urn/resource/catalog/mvpd'
        : 'https://data-store.unowcms-stag.quickplay.com/content/urn/resource/catalog/mvpd';
  }

  get mvpd(): string {
    return this.userMetadata?.data?.mvpd;
  }

  get userId(): string {
    return this.userMetadata?.data.userID;
  }

  get entitlementPackages(): string[] {
    return this.authorizedEntitlements;
  }

  get fullEntitlementPackages(): string[] {
    if (!this.authorizedEntitlements) return this.authorizedEntitlements;
    return this.authorizedEntitlements.map((e) => `urn:package:tve:${e}`);
  }

  get mediaToken(): string {
    return this.shortMediaToken;
  }

  get mvpdImg(): string {
    return this.mvpdImageSrc;
  }

  get expiresIn(): number {
    return parseInt(this.clientToken?.expires_in ?? '0', 10);
  }

  // Return device activation code after needed session set.
  public getDeviceCode(): Observable<RegCodeResponse> {
    if (this.clientToken) {
      return this.getRegCode();
    }
    return this.setRegister().pipe(
      switchMap(() => {
        return this.getRegCode();
      }),
      catchError((error) => {
        console.error('Error:', error);
        return of(null as unknown as RegCodeResponse); // Prevents errors from breaking the stream
      })
    );
  }

  // User for device activation code. Validate every x ms this call.
  public pollForToken(deviceCode: string): Observable<unknown> {
    const authHeaders = new HttpHeaders({
      Authorization: `Bearer ${this.clientToken.access_token}`,
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
    });
    return this.http.get(`${this.apiUrl}/api/v1/checkauthn?reg_code=${deviceCode}&requestor_id=${this.requestorId}`, {
      headers: authHeaders,
    });
  }

  // Get the needed user data info
  public getAllUserData(): Observable<QPATResponse> {
    return forkJoin([
      this.getUserId().pipe(
        tap((response) => this.userResponse.next(response)),
        catchError((error) => {
          console.error('getUserId failed:', error);
          return of(null); // Returning null instead of failing the whole stream
        })
      ),
      this.getPreauthorize().pipe(
        tap((response) => (this.authorizedEntitlements = response.resources.filter((res) => res.authorized).map((res) => res.id))),
        catchError((error) => {
          console.error('getPreauthorize failed:', error);
          return of(null);
        })
      ),
      this.getUserMetadata().pipe(
        tap((response) => {
          this.userMetadata = response;
          this.localStorage.setItem(LocalStorageKeys.USER_ID, this.userId);
        }),
        catchError((error) => {
          console.error('getUserMetadata failed:', error);
          return of(null);
        })
      ),
    ]).pipe(
      switchMap(() => {
        return this.getMVPDsList();
      }),
      tap((result: MVPDData) => {
        const mvpdSelected = result.data.find((mvpd) => mvpd.mso_id === this.userMetadata.data.mvpd);
        const { id, ia, mso_hash, name } = mvpdSelected;
        this.newrelic.store.setTveProvider(name);
        this.newrelic.store.setTveProvider(id);
        this.mvpdHash = mso_hash;
        this.mvpdImageSrc = `${this.configService.applicationConfig.imageResizeURL}/image/${id}/${
          ia && ia.length > 0 ? ia.find((ar) => ar.includes('16x9')) ?? '0-16x9' : '0-16x9'
        }.png?width=210`;
      }),
      switchMap(() => {
        return from(this.qpatService.getQPAT());
      }),
      tap((result: QPATResponse) => {
        saveXAuthToken(result, this.localStorage);
      })
    );
  }

  /**
   * Both calls are needed to init the adobe service for logged in users
   *   Register the application using the software statement
   *   Get user access token
   *
   * @returns ClientToken
   */
  public setRegister(): Observable<ClientToken> {
    return this.register().pipe(
      tap((response) => {
        this.clientRegistration = response;
      }),
      switchMap(() => {
        // Call getToken to get the token, and set it after completion
        return this.getToken();
      }),
      tap((tokenResponse) => {
        // Set the token after getToken() is done
        this.clientToken = tokenResponse;
        this.startRefreshTokenTimer();
      })
    );
  }

  /**
   * Validate if user is still with valid token
   * Get all user data if still valid. Force logout if invalid.
   */
  public silentLogin(): Observable<unknown> {
    return this.setRegister().pipe(
      switchMap(() =>
        this.checkAuthn().pipe(
          switchMap(() => this.getAllUserData()),
          catchError(() => {
            console.error('Authentication failed: 404 Not Found');
            this.cleanUserVariables();
            sessionStorage.removeItem('xAuthToken');
            this.localStorage.removeItem(LocalStorageKeys.AUTHDATA);
            this.localStorage.removeItem(LocalStorageKeys.USER_ID);
            return of(null); // Fallback value
          })
        )
      )
    );
  }

  // Logout user session
  public logout(): Observable<unknown> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Bearer ${this.clientToken.access_token}`,
    });

    return this.http
      .delete<unknown>(
        `${this.apiUrl}/api/v1/logout?deviceId=${this.deviceHandler.getCurrentDeviceID()}&requestor_id=${this.requestorId}`,
        { headers }
      )
      .pipe(
        tap(() => {
          this.authorizedEntitlements = undefined;
        })
      );
  }

  /**
   * Trigger setToken for short-lived media token
   * Return true if user is entitled
   *        false if package is not entitled
   */
  public userIsEntitled = async (resourcesIds: string[]): Promise<boolean> => {
    const cleanedResources = resourcesIds.map((resource) => resource.replace('urn:package:tve:', ''));
    for (const resource of cleanedResources) {
      // Obtain token with first package that is included in pre-authorized packages
      if (this.entitlementPackages?.includes(resource)) {
        await this.authorizePlayer(resource).toPromise();
        return true;
      }
    }
    return false;
  };

  // Check if user has valid session active
  public checkAuthn(): Observable<unknown> {
    const authHeaders = new HttpHeaders({
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      Authorization: `Bearer ${this.clientToken.access_token}`,
    });
    return this.http.get<UserMetadata>(
      `${this.apiUrl}/api/v1/checkauthn?deviceId=${this.deviceHandler.getCurrentDeviceID()}&requestor=${this.requestorId}`,
      {
        headers: authHeaders,
      }
    );
  }

  public cleanUserVariables(): void {
    this.userMetadata = undefined;
    this.authorizedEntitlements = [];
    this.userResponse.next(this.userResponseDefault);
    this.mvpdImageSrc = undefined;
    this.mvpdHash = undefined;
    this.stopRefreshTokenTimer();
  }

  public getUserInfo(): {
    mvpdHash: string;
    userID: string;
  } {
    return {
      mvpdHash: this.mvpdHash,
      userID: this.userId,
    };
  }

  private getMVPDsList(): Observable<MVPDData> {
    return this.http.get<MVPDData>(`${this.mvpdConfigListUrl}?client=${getXClientId()}&st=published,draft&pageSize=50`);
  }

  private authorizePlayer(resource: string): Observable<unknown> {
    return this.checkAuthorize(resource).pipe(
      switchMap(() => {
        // Call getToken to get the token, and set it after completion
        return this.getShortMediaToken(resource);
      }),
      tap((tokenResponse: ShortMediaTokenResponse) => {
        this.shortMediaToken = tokenResponse.serializedToken;
      })
    );
  }

  // Authorize user playback
  private checkAuthorize(resource: string): Observable<unknown> {
    const authHeaders = new HttpHeaders({
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      Authorization: `Bearer ${this.clientToken.access_token}`,
    });
    return this.http.get<UserMetadata>(
      `${this.apiUrl}/api/v1/authorize?deviceId=${this.deviceHandler.getCurrentDeviceID()}&requestor=${
        this.requestorId
      }&resource=${resource}`,
      {
        headers: authHeaders,
      }
    );
  }

  // Get short media token
  private getShortMediaToken(resource: string): Observable<ShortMediaTokenResponse> {
    const authHeaders = new HttpHeaders({
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      Authorization: `Bearer ${this.clientToken.access_token}`,
    });
    return this.http.get<ShortMediaTokenResponse>(
      `${this.apiUrl}/api/v1/mediatoken?deviceId=${this.deviceHandler.getCurrentDeviceID()}&requestor=${
        this.requestorId
      }&resource=${resource}`,
      {
        headers: authHeaders,
      }
    );
  }

  // Get user id and expires in
  private getUserId(): Observable<UserResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Bearer ${this.clientToken.access_token}`,
    });

    return this.http.get<UserResponse>(
      `${this.apiUrl}/api/v1/tokens/authn?deviceId=${this.deviceHandler.getCurrentDeviceID()}&requestor_id=${this.requestorId}`,
      { headers }
    );
  }

  // Validate user authorized resources packages
  private getPreauthorize(): Observable<PreauthorizedResourcesResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      Authorization: `Bearer ${this.clientToken.access_token}`,
    });

    return this.http.get<PreauthorizedResourcesResponse>(
      `${this.apiUrl}/api/v1/preauthorize?deviceId=${this.deviceHandler.getCurrentDeviceID()}&requestor=${
        this.requestorId
      }&resource=${this.preAuthorizedResources.join(',')}&format=json`,
      { headers }
    );
  }

  // Get user metadata
  private getUserMetadata(): Observable<UserMetadata> {
    const authHeaders = new HttpHeaders({
      Authorization: `Bearer ${this.clientToken.access_token}`,
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
    });
    return this.http.get<UserMetadata>(
      `${this.apiUrl}/api/v1/tokens/usermetadata?deviceId=${this.deviceHandler.getCurrentDeviceID()}&requestor=${this.requestorId}`,
      {
        headers: authHeaders,
      }
    );
  }

  // Get client id and client secret
  private register(): Observable<ClientRegistration> {
    const body = { software_statement: this.softwareStatement };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post<ClientRegistration>(`${this.apiUrl}/o/client/register`, body, { headers });
  }

  // Get client acccess token
  private getToken(): Observable<ClientToken> {
    const body = new URLSearchParams();
    body.set('grant_type', 'client_credentials');
    body.set('client_id', this.clientRegistration.client_id);
    body.set('client_secret', this.clientRegistration.client_secret);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
    });

    return this.http.post<ClientToken>(`${this.apiUrl}/o/client/token`, body.toString(), { headers });
  }

  // Get device activation code
  private getRegCode(): Observable<RegCodeResponse> {
    const body = new URLSearchParams();
    body.set('requestor', this.requestorId);
    body.set('deviceId', this.deviceHandler.getCurrentDeviceID());
    body.set('format', 'json');

    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.clientToken.access_token}`,
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      'Content-Type': 'application/x-www-form-urlencoded',
      // Origin: 'https://tv.univision.com',
      // Referrer: 'https://tv.univision.com/',
    });

    return this.http.post<RegCodeResponse>(`${this.apiUrl}/reggie/v1/univision/regcode`, body.toString(), { headers });
  }

  /**
   * Refresh token
   */
  private refreshToken(): void {
    const getToken: Subscription = this.getToken()
      .pipe(
        tap((tokenResponse) => {
          this.clientToken = tokenResponse;
          this.startRefreshTokenTimer();
        })
      )
      .subscribe(); // This ensures the request is actually made
    this.subscriptionHandlerService.addSubscription(this.serviceName, 'getToken', getToken);
  }

  private startRefreshTokenTimer(): void {
    try {
      this.stopRefreshTokenTimer();

      const timeUntilExpiry = this.expiresIn;
      const refreshTime = timeUntilExpiry * 0.8;

      if (refreshTime <= 0) {
        this.refreshToken();
        return;
      }
      this.refreshTokenTimeout = setTimeout(() => {
        this.refreshToken();
      }, refreshTime * 1000);
    } catch {}
  }

  private stopRefreshTokenTimer(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }
  }
}
